export type EdoctaListItem = {
  clv_sistema: string;
  clv_subcuenta: string;
  fecha_oper: string;
  fecha_emis: string | null;
  file_name: string | null;
  cargos: string;
  creditos: string;
  total_neto_dia: string;
  descargado_en: string | null;
};

export type EdoctaListResponse = {
  items: EdoctaListItem[];
  total: number;
  sum_cargos: string;
  sum_creditos: string;
  sum_total_neto: string;
};

export type Catalogos = {
  sistemas: string[];
  subcuentas: { clv_subcuenta: string; clv_sistema: string }[];
  fecha_min: string | null;
  fecha_max: string | null;
};

export type EdoctaFilters = {
  sistema?: string;
  subcuenta?: string;
  desde?: string;
  hasta?: string;
  sort?: string;
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

import { acquireIdToken, DEV_BYPASS_AUTH, renovarSesion } from "./auth";

async function authHeaders(): Promise<Record<string, string>> {
  if (DEV_BYPASS_AUTH) return {}; // dev: el backend usa DEV_USER_EMAIL
  const token = await acquireIdToken();
  return { Authorization: `Bearer ${token}` };
}

/** fetch autenticado; si el backend contesta 401 la sesión murió → al login. */
export async function fetchAuth(url: string, init: RequestInit = {}): Promise<Response> {
  const auth = await authHeaders();
  const res = await fetch(url, {
    credentials: "include",
    ...init,
    headers: { ...auth, ...(init.headers as Record<string, string> | undefined) },
  });
  if (res.status === 401) await renovarSesion();
  return res;
}

/** Mensaje de error legible: el `detail` de FastAPI si viene, si no el status. */
export async function detalleDe(res: Response): Promise<string> {
  let detail = `${res.status} ${res.statusText}`.trim();
  try {
    const j = await res.json();
    if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
  } catch {
    /* sin cuerpo JSON */
  }
  return detail;
}

export async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetchAuth(url, init);
  if (!res.ok) throw new Error(await detalleDe(res));
  return res.json();
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetchAuth(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* sin cuerpo JSON */
    }
    throw new Error(detail);
  }
  return res.json();
}

export function listEdoctas(filters: EdoctaFilters): Promise<EdoctaListResponse> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== "") params.set(k, String(v));
  });
  const qs = params.toString();
  return jsonFetch<EdoctaListResponse>(`/api/edoctas${qs ? `?${qs}` : ""}`);
}

export function getCatalogos(): Promise<Catalogos> {
  return jsonFetch<Catalogos>("/api/edoctas/catalogos");
}

export function getMe(): Promise<{ email: string; name: string }> {
  return jsonFetch("/api/me");
}

// ---------- Registro de proveedores (admin) --------------------------------

export type RegistroInvite = {
  id: number;
  email: string;
  etiqueta: string | null;
  creado_por: string;
  creado_en: string;
  expira_en: string;
  usado_en: string | null;
  revocado_en: string | null;
  submission_id: number | null;
  status: "valid" | "used" | "expired" | "revoked" | "not_found";
};

export type RegistroInviteCreated = {
  id: number;
  email: string;
  etiqueta: string | null;
  link: string;
  expira_en: string;
  creado_en: string;
  correo_enviado: boolean;
  correo_detalle: string | null;
};

export type CuentaBancaria = {
  banco: string | null;
  titular: string | null;
  cuenta: string | null;
  clabe: string | null;
  swift: string | null;
  moneda: string | null;
};

export type RegistroSubmissionRow = {
  id: number;
  invite_id: number;
  razon_social: string;
  rfc: string;
  contacto_nombre: string;
  contacto_correo: string;
  num_cuentas: number;
  monedas: string | null;
  enviado_en: string;
};

export type RegistroSubmissionDetail = RegistroSubmissionRow & {
  nombre_comercial: string | null;
  regimen_fiscal: string | null;
  giro: string | null;
  direccion_fiscal: string | null;
  cp: string | null;
  tipo_vialidad: string | null;
  nombre_vialidad: string | null;
  num_exterior: string | null;
  num_interior: string | null;
  colonia: string | null;
  localidad: string | null;
  municipio: string | null;
  entidad: string | null;
  entre_calle: string | null;
  y_calle: string | null;
  contacto_cargo: string | null;
  contacto_telefono: string | null;
  cuentas: CuentaBancaria[];
  ip: string | null;
  csf_disponible: boolean;
  caratula_disponible: boolean;
};

export function crearInvite(email: string, etiqueta: string): Promise<RegistroInviteCreated> {
  return postJson("/api/registro/invites", { email, etiqueta: etiqueta || null });
}

export function listarInvites(): Promise<RegistroInvite[]> {
  return jsonFetch("/api/registro/invites");
}

export function revocarInvite(id: number): Promise<{ ok: boolean }> {
  return postJson(`/api/registro/invites/${id}/revoke`, {});
}

export function listarSubmissions(): Promise<RegistroSubmissionRow[]> {
  return jsonFetch("/api/registro/submissions");
}

export function getSubmission(id: number): Promise<RegistroSubmissionDetail> {
  return jsonFetch(`/api/registro/submissions/${id}`);
}

/** Descarga un adjunto del alta (csf|caratula) con fetch autenticado + save dialog. */
export async function descargarArchivoRegistro(id: number, kind: "csf" | "caratula"): Promise<void> {
  const res = await fetchAuth(`/api/registro/submissions/${id}/archivo/${kind}`);
  if (!res.ok) throw new Error(`Descarga falló: ${res.status} ${res.statusText}`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  a.download = match ? match[1] : `${kind}_${id}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------- Vacaciones del equipo (banner) ---------------------------------

export type VacacionEquipo = {
  nombre: string;
  fecha_inicio: string; // YYYY-MM-DD
  fecha_fin: string; // YYYY-MM-DD
  en_curso: boolean;
};

export function getVacacionesEquipo(): Promise<VacacionEquipo[]> {
  return jsonFetch("/api/vacaciones-equipo");
}

// ---------- Monitoreo REA ---------------------------------------------------

export type MonitoreoRea = {
  rea_vs_mgp_pct: string | null;
  bullet_color: string | null;
  creado_en: string | null;
};

export function getMonitoreoRea(): Promise<MonitoreoRea> {
  return jsonFetch<MonitoreoRea>("/api/monitoreo/rea");
}

export type TipoCambio = {
  fecha: string | null;
  valor: string | null;
};

export function getTipoCambio(): Promise<TipoCambio> {
  return jsonFetch<TipoCambio>("/api/monitoreo/tipo-cambio");
}

export type Hsc = {
  fecha: string | null;
  valor: string | null;
  fecha_prev: string | null;
  valor_prev: string | null;
};

export function getHsc(): Promise<Hsc> {
  return jsonFetch<Hsc>("/api/monitoreo/hsc");
}

export function downloadUrl(subcuenta: string, fecha: string, kind: "pdf" | "xml"): string {
  return `/api/edoctas/${subcuenta}/${fecha}/download/${kind}`;
}

/**
 * Descarga un PDF/XML usando fetch autenticado y triggerea save dialog.
 * Soporta tanto 302 → signed URL (GCS) como streaming directo del backend.
 */
export async function downloadEdocta(
  subcuenta: string,
  fecha: string,
  kind: "pdf" | "xml",
): Promise<void> {
  const url = downloadUrl(subcuenta, fecha, kind);
  const res = await fetchAuth(url);
  if (!res.ok) {
    throw new Error(`Descarga falló: ${res.status} ${res.statusText}`);
  }

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;

  // Intentar usar el filename del Content-Disposition; si no, construir uno.
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  a.download = match ? match[1] : `${subcuenta}-${fecha}.${kind}`;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

/** Descarga un ZIP con el PDF y XML de los estados de cuenta seleccionados. */
export async function downloadEdoctasZip(
  items: { subcuenta: string; fecha: string }[],
): Promise<void> {
  const res = await fetchAuth("/api/edoctas/zip", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    throw new Error(`Descarga falló: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  a.download = match ? match[1] : "edoctas.zip";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------- Detalle ----------------------------------------------------------

export type Concepto = {
  ful: string;
  grupo: string | null;
  descripcion: string | null;
  monto_total: string | null;
  iva: string | null;
  total_neto: string | null;
};

export type Factura = {
  fuf: string;
  num_liq: number;
  tipo: string | null;
  emisor: string | null;
  rfc: string | null;
  razon_social: string | null;
  fecha_pago: string | null;
  ref_bancaria: string | null;
  conceptos: Concepto[];
};

export type Cabecera = {
  fuecd: string;
  clv_participante: string;
  clv_sistema: string;
  clv_subcuenta: string;
  fecha_oper: string;
  fecha_emis: string | null;
  file_name: string | null;
  descargado_en: string | null;
  cargos: string;
  creditos: string;
  total_neto_dia: string;
};

export type EdoctaDetalle = {
  cabecera: Cabecera;
  facturas: Factura[];
};

export function getEdoctaDetalle(
  subcuenta: string,
  fecha: string,
): Promise<EdoctaDetalle> {
  return jsonFetch<EdoctaDetalle>(`/api/edoctas/${subcuenta}/${fecha}`);
}

// ---------- Telemetría horaria ----------------------------------------------

export type RegistroHorario = {
  hora: number;
  elemento: string;
  nodo: string | null;
  potencia: string | null;
  potencia_mda: string | null;
  potencia_mtr: string | null;
  precio: string | null;
  monto_horario: string | null;
};

export type TelemetriaHoraria = {
  ful: string;
  elementos: string[];
  registros: RegistroHorario[];
};

export function getTelemetriaHoraria(ful: string): Promise<TelemetriaHoraria> {
  return jsonFetch<TelemetriaHoraria>(`/api/conceptos/${ful}/horario`);
}

// ---------- PML / PND -------------------------------------------------------

export type Mercado = "MDA" | "MTR";
export type NodoTipo = "PML" | "PND";

export type PmlNodo = {
  tipo: NodoTipo;
  clave: string;
  sistema: string;
  nombre: string | null;
  zona_carga: string | null;
};

export type PmlCatalogos = {
  sistemas: string[];
  fecha_min: string | null;
  fecha_max: string | null;
};

export type PmlPrecioRow = {
  tipo: NodoTipo;
  clave: string;
  sistema: string;
  fecha: string;
  hora: number;
  precio: string | null;
  ene: string | null;
  per: string | null;
  cng: string | null;
};

export type PmlPreciosResponse = {
  items: PmlPrecioRow[];
  total: number;
};

export type PmlPreciosFilters = {
  mercado: Mercado;
  nodos: { tipo: NodoTipo; clave: string }[];
  desde?: string;
  hasta?: string;
  sort?: string;
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export function getPmlCatalogos(mercado: Mercado): Promise<PmlCatalogos> {
  return jsonFetch<PmlCatalogos>(`/api/pml/catalogos?mercado=${mercado}`);
}

export function getPmlNodos(opts: {
  tipo?: NodoTipo | "ALL";
  sistema?: string;
  search?: string;
  limit?: number;
}): Promise<{ items: PmlNodo[] }> {
  const params = new URLSearchParams();
  if (opts.tipo) params.set("tipo", opts.tipo);
  if (opts.sistema) params.set("sistema", opts.sistema);
  if (opts.search) params.set("search", opts.search);
  if (opts.limit) params.set("limit", String(opts.limit));
  return jsonFetch<{ items: PmlNodo[] }>(`/api/pml/nodos?${params.toString()}`);
}

// ---------- Mapa de calor PND -----------------------------------------------

export type MapaCalorRow = {
  zona_carga: string;
  sistema: string;
  precio_promedio: string | null;
  n_obs: number;
};

export type MapaCalorResponse = {
  items: MapaCalorRow[];
  mercado: Mercado;
  fecha_min_efectiva: string | null;
  fecha_max_efectiva: string | null;
  precio_min: string | null;
  precio_max: string | null;
};

export function getMapaCalor(opts: {
  mercado: Mercado;
  desde?: string;
  hasta?: string;
  sistema?: string;
  hora?: number | null;
}): Promise<MapaCalorResponse> {
  const params = new URLSearchParams();
  params.set("mercado", opts.mercado);
  if (opts.desde) params.set("desde", opts.desde);
  if (opts.hasta) params.set("hasta", opts.hasta);
  if (opts.sistema) params.set("sistema", opts.sistema);
  if (opts.hora != null) params.set("hora", String(opts.hora));
  return jsonFetch<MapaCalorResponse>(`/api/pml/mapa-calor?${params.toString()}`);
}

export function getPmlPrecios(f: PmlPreciosFilters): Promise<PmlPreciosResponse> {
  const params = new URLSearchParams();
  params.set("mercado", f.mercado);
  params.set("nodos", f.nodos.map((n) => `${n.tipo}:${n.clave}`).join(","));
  if (f.desde) params.set("desde", f.desde);
  if (f.hasta) params.set("hasta", f.hasta);
  if (f.sort) params.set("sort", f.sort);
  if (f.order) params.set("order", f.order);
  if (f.limit != null) params.set("limit", String(f.limit));
  if (f.offset != null) params.set("offset", String(f.offset));
  return jsonFetch<PmlPreciosResponse>(`/api/pml/precios?${params.toString()}`);
}

// ---------- Resumen de nodos (promedios anual / mensual / horario) ----------

export type PmlResumenRow = {
  tipo: NodoTipo;
  clave: string;
  anio: number | null;
  mes: number | null;
  hora: number | null;
  precio: string | null;
  n_obs: number;
};

export type PmlResumenResponse = {
  anual: PmlResumenRow[];
  mensual: PmlResumenRow[];
  horario: PmlResumenRow[];
};

export function getPmlResumen(
  f: Omit<PmlPreciosFilters, "sort" | "order" | "limit" | "offset">,
): Promise<PmlResumenResponse> {
  const params = new URLSearchParams();
  params.set("mercado", f.mercado);
  params.set("nodos", f.nodos.map((n) => `${n.tipo}:${n.clave}`).join(","));
  if (f.desde) params.set("desde", f.desde);
  if (f.hasta) params.set("hasta", f.hasta);
  return jsonFetch<PmlResumenResponse>(`/api/pml/resumen?${params.toString()}`);
}

// ---------- Zonas de carga agrupadas ----------------------------------------

export type ZcaItem = {
  zona_carga_agrupada: string;
  zonas_pnd: number;
  suma_porcentaje: string;
};

export type ZcaPrecioRow = {
  zca: string;
  mercado: "MDA" | "MTR";
  fecha: string;
  hora: number | null;
  precio: string | null;
  ene: string | null;
  per: string | null;
  cng: string | null;
};

export type ZcaPreciosResponse = {
  items: ZcaPrecioRow[];
  granularidad: "horaria" | "diaria";
};

export type ZcaPreciosFilters = {
  zcas: string[];
  desde?: string;
  hasta?: string;
  mercados: ("MDA" | "MTR")[];
  granularidad: "auto" | "horaria" | "diaria";
};

export function getZcaCatalogo(): Promise<{ items: ZcaItem[] }> {
  return jsonFetch<{ items: ZcaItem[] }>("/api/pml/zca/catalogo");
}

export type ZcaDesgloseItem = {
  zona_carga: string;
  porcentaje: string;
  precio: string | null;
  ene: string | null;
  per: string | null;
  cng: string | null;
};

export type ZcaDesgloseResponse = {
  zca: string;
  mercado: "MDA" | "MTR";
  fecha: string;
  hora: number | null;
  items: ZcaDesgloseItem[];
  suma_porcentaje: string;
  precio_total: string | null;
  ene_total: string | null;
  per_total: string | null;
  cng_total: string | null;
};

export function getZcaDesglose(opts: {
  zca: string;
  mercado: "MDA" | "MTR";
  fecha: string;
  hora?: number | null;
}): Promise<ZcaDesgloseResponse> {
  const params = new URLSearchParams();
  params.set("zca", opts.zca);
  params.set("mercado", opts.mercado);
  params.set("fecha", opts.fecha);
  if (opts.hora != null) params.set("hora", String(opts.hora));
  return jsonFetch<ZcaDesgloseResponse>(`/api/pml/zca/desglose?${params.toString()}`);
}

export function getZcaPrecios(f: ZcaPreciosFilters): Promise<ZcaPreciosResponse> {
  const params = new URLSearchParams();
  params.set("zcas", f.zcas.join(","));
  params.set("mercados", f.mercados.join(","));
  params.set("granularidad", f.granularidad);
  if (f.desde) params.set("desde", f.desde);
  if (f.hasta) params.set("hasta", f.hasta);
  return jsonFetch<ZcaPreciosResponse>(`/api/pml/zca/precios?${params.toString()}`);
}

export async function downloadZcaPreciosCsv(f: ZcaPreciosFilters): Promise<void> {
  const params = new URLSearchParams();
  params.set("zcas", f.zcas.join(","));
  params.set("mercados", f.mercados.join(","));
  params.set("granularidad", f.granularidad);
  if (f.desde) params.set("desde", f.desde);
  if (f.hasta) params.set("hasta", f.hasta);

  const res = await fetchAuth(`/api/pml/zca/precios.csv?${params.toString()}`);
  if (!res.ok) {
    throw new Error(`Descarga falló: ${res.status} ${res.statusText}`);
  }

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;

  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  a.download = match ? match[1] : `zca_precios.csv`;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

export async function downloadPmlPreciosCsv(
  f: Omit<PmlPreciosFilters, "limit" | "offset">,
): Promise<void> {
  const params = new URLSearchParams();
  params.set("mercado", f.mercado);
  params.set("nodos", f.nodos.map((n) => `${n.tipo}:${n.clave}`).join(","));
  if (f.desde) params.set("desde", f.desde);
  if (f.hasta) params.set("hasta", f.hasta);
  if (f.sort) params.set("sort", f.sort);
  if (f.order) params.set("order", f.order);

  const res = await fetchAuth(`/api/pml/precios.csv?${params.toString()}`);
  if (!res.ok) {
    throw new Error(`Descarga falló: ${res.status} ${res.statusText}`);
  }

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;

  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  a.download = match ? match[1] : `precios_${f.mercado}.csv`;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------- Bloomberg -------------------------------------------------------

export type BloombergTicker = {
  ticker: string;
  alias: string | null;
  fecha_min: string | null;
  fecha_max: string | null;
  n_obs: number;
};

export type BloombergCatalogoResponse = { items: BloombergTicker[] };

export type BloombergPrecioRow = {
  fecha: string;
  ticker: string;
  px_last: string | null;
};

export type BloombergPreciosResponse = {
  items: BloombergPrecioRow[];
  desde: string | null;
  hasta: string | null;
};

export function getBloombergCatalogo(): Promise<BloombergCatalogoResponse> {
  return jsonFetch<BloombergCatalogoResponse>("/api/bloomberg/catalogo");
}

export function getBloombergPrecios(opts: {
  tickers: string[];
  desde?: string;
  hasta?: string;
}): Promise<BloombergPreciosResponse> {
  const params = new URLSearchParams();
  params.set("tickers", opts.tickers.join(","));
  if (opts.desde) params.set("desde", opts.desde);
  if (opts.hasta) params.set("hasta", opts.hasta);
  return jsonFetch<BloombergPreciosResponse>(
    `/api/bloomberg/precios?${params.toString()}`,
  );
}

export async function downloadBloombergCsv(opts: {
  tickers: string[];
  desde?: string;
  hasta?: string;
}): Promise<void> {
  const params = new URLSearchParams();
  params.set("tickers", opts.tickers.join(","));
  if (opts.desde) params.set("desde", opts.desde);
  if (opts.hasta) params.set("hasta", opts.hasta);

  const res = await fetchAuth(`/api/bloomberg/precios.csv?${params.toString()}`);
  if (!res.ok) {
    throw new Error(`Descarga falló: ${res.status} ${res.statusText}`);
  }

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  a.download = match ? match[1] : "bloomberg_prices.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------- Demanda ---------------------------------------------------------

export type DemandaSistema = "SIN" | "BCA" | "BCS";

export type DemandaMesRow = {
  anio: number;
  mes: number;
  suma_mwh: string | null;
  pico_mwh: string | null;
  liquidacion_min: number | null;
};

export type DemandaDashboardResponse = {
  sistema: DemandaSistema;
  desde: string;
  items: DemandaMesRow[];
};

export function getDemandaDashboard(opts: {
  sistema: DemandaSistema;
  anios?: number;
}): Promise<DemandaDashboardResponse> {
  const params = new URLSearchParams();
  params.set("sistema", opts.sistema);
  if (opts.anios != null) params.set("anios", String(opts.anios));
  return jsonFetch<DemandaDashboardResponse>(
    `/api/demanda/dashboard?${params.toString()}`,
  );
}

export type DemandaDiaRow = {
  anio: number;
  mes: number;
  dia: number;
  suma_mwh: string | null;
  pico_mwh: string | null;
  liquidacion_min: number | null;
  fuente: "balance" | "horaria";
};

export type DemandaDiarioResponse = {
  sistema: DemandaSistema;
  desde: string;
  items: DemandaDiaRow[];
};

export function getDemandaDiario(opts: {
  sistema: DemandaSistema;
  anios?: number;
}): Promise<DemandaDiarioResponse> {
  const params = new URLSearchParams();
  params.set("sistema", opts.sistema);
  if (opts.anios != null) params.set("anios", String(opts.anios));
  return jsonFetch<DemandaDiarioResponse>(
    `/api/demanda/diario?${params.toString()}`,
  );
}

export type DemandaTopHoraRow = {
  anio: number;
  fecha: string;
  hora: number;
  demanda_mwh: string | null;
  liquidacion_min: number | null;
  posicion: number; // 1 = la hora de mayor demanda del año
  fuente: "balance" | "horaria";
};

export type DemandaTopHorasResponse = {
  sistema: DemandaSistema;
  desde: string;
  n: number;
  items: DemandaTopHoraRow[];
};

export function getDemandaTopHoras(opts: {
  sistema: DemandaSistema;
  anios?: number;
  n?: number;
}): Promise<DemandaTopHorasResponse> {
  const params = new URLSearchParams();
  params.set("sistema", opts.sistema);
  if (opts.anios != null) params.set("anios", String(opts.anios));
  if (opts.n != null) params.set("n", String(opts.n));
  return jsonFetch<DemandaTopHorasResponse>(
    `/api/demanda/top-horas?${params.toString()}`,
  );
}

export type DemandaHorariaRow = {
  fecha: string;
  hora: number;
  region: string; // '-' = total del sistema; área (Central, Noreste…) en desglose
  demanda_mw: number | null;
  generacion_mw: number | null;
  pronostico_mw: number | null;
  actualizado_en: string | null;
};

export type DemandaHorariaResponse = {
  sistema: DemandaSistema;
  desde: string;
  hasta: string | null;
  desglose: boolean;
  regiones: string[];
  items: DemandaHorariaRow[];
  ultima_actualizacion: string | null;
};

export function getDemandaHoraria(opts: {
  sistema: DemandaSistema;
  desde?: string;
  hasta?: string;
  desglose?: boolean;
}): Promise<DemandaHorariaResponse> {
  const params = new URLSearchParams();
  params.set("sistema", opts.sistema);
  if (opts.desde) params.set("desde", opts.desde);
  if (opts.hasta) params.set("hasta", opts.hasta);
  if (opts.desglose) params.set("desglose", "true");
  return jsonFetch<DemandaHorariaResponse>(
    `/api/demanda-horaria/serie?${params.toString()}`,
  );
}

// ---------- Tipo de cambio --------------------------------------------------

export type TipoCambioMoneda = {
  moneda: string;
  fecha_min: string | null;
  fecha_max: string | null;
  n_obs: number;
};

export type TipoCambioCatalogoResponse = { items: TipoCambioMoneda[] };

export type TipoCambioRow = {
  fecha: string;
  serie: string;
  moneda: string;
  valor: string | null;
};

export type TipoCambioSerieResponse = {
  moneda: string;
  desde: string | null;
  hasta: string | null;
  items: TipoCambioRow[];
};

export function getTipoCambioCatalogo(): Promise<TipoCambioCatalogoResponse> {
  return jsonFetch<TipoCambioCatalogoResponse>("/api/tipo-cambio/catalogo");
}

export function getTipoCambioSerie(opts: {
  moneda: string;
  desde?: string;
  hasta?: string;
}): Promise<TipoCambioSerieResponse> {
  const params = new URLSearchParams();
  params.set("moneda", opts.moneda);
  if (opts.desde) params.set("desde", opts.desde);
  if (opts.hasta) params.set("hasta", opts.hasta);
  return jsonFetch<TipoCambioSerieResponse>(
    `/api/tipo-cambio/serie?${params.toString()}`,
  );
}

export async function downloadTipoCambioCsv(opts: {
  moneda: string;
  desde?: string;
  hasta?: string;
}): Promise<void> {
  const params = new URLSearchParams();
  params.set("moneda", opts.moneda);
  if (opts.desde) params.set("desde", opts.desde);
  if (opts.hasta) params.set("hasta", opts.hasta);
  const res = await fetchAuth(`/api/tipo-cambio/serie.csv?${params.toString()}`);
  if (!res.ok) {
    throw new Error(`Descarga falló: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  a.download = match ? match[1] : `tipo_cambio_${opts.moneda}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------- Tarifas CFE -----------------------------------------------------

export type TarifaCfeRow = {
  tarifa: string;
  anio: number;
  mes: number;
  division: string;
  cargo: string;
  intervalo: string;
  unidades: string | null;
  valor: string | null;
};

export type PeriodoTarifa = { anio: number; mes: number };

export type TarifaCfeCatalogos = {
  tarifas: string[];
  divisiones: string[];
  periodos: PeriodoTarifa[];
};

export function getTarifasCfeCatalogos(): Promise<TarifaCfeCatalogos> {
  return jsonFetch<TarifaCfeCatalogos>("/api/tarifas-cfe/catalogos");
}

export function getTarifasCfe(opts: {
  tarifa: string;
  desde: string; // YYYY-MM
  hasta: string; // YYYY-MM
  division?: string;
}): Promise<TarifaCfeRow[]> {
  const params = new URLSearchParams();
  params.set("tarifa", opts.tarifa);
  params.set("desde", opts.desde);
  params.set("hasta", opts.hasta);
  if (opts.division) params.set("division", opts.division);
  return jsonFetch<TarifaCfeRow[]>(`/api/tarifas-cfe?${params.toString()}`);
}

// ---------- Contratos de cobertura ------------------------------------------

export type ContratoCatalogos = {
  emisores: string[];
  adquirientes: string[];
  estatus: string[];
  fecha_firma_min: string | null;
  fecha_firma_max: string | null;
};

export type ContratoDashboardRow = {
  anio: number;
  emisor: string | null;
  adquiriente: string | null;
  total: number;
};

export type ContratoListItem = {
  id_contrato: number;
  folio: string | null;
  folio_real: string | null;
  emisor: string | null;
  adquiriente: string | null;
  fecha_firma: string | null;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  estatus: string | null;
  tipo_contrato: string | null;
};

export type ContratoGanttRow = {
  id_contrato: number;
  folio: string | null;
  folio_real: string | null;
  emisor: string | null;
  adquiriente: string | null;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  estatus: string | null;
  tipo_contrato: string | null;
};

export type ContratoDashboardFilters = {
  desde?: string;
  hasta?: string;
  emisores?: string[];
  adquirientes?: string[];
  estatus?: string[];
};

export type ContratoGanttFilters = {
  emisores?: string[];
  adquirientes?: string[];
  estatus?: string[];
  desde?: string;
  hasta?: string;
  agrupar_por?: "emisor" | "adquiriente";
  limit?: number;
  offset?: number;
};

/** Multi-valor HTTP: ?key=A&key=B. Para valores que pueden contener comas. */
function appendMultiParam(p: URLSearchParams, key: string, values?: string[]) {
  if (!values) return;
  for (const v of values) {
    if (v) p.append(key, v);
  }
}

export function getContratosCatalogos(): Promise<ContratoCatalogos> {
  return jsonFetch<ContratoCatalogos>("/api/contratos/catalogos");
}

export function getContratosDashboard(
  f: ContratoDashboardFilters,
): Promise<{ items: ContratoDashboardRow[] }> {
  const params = new URLSearchParams();
  if (f.desde) params.set("desde", f.desde);
  if (f.hasta) params.set("hasta", f.hasta);
  appendMultiParam(params, "emisores", f.emisores);
  appendMultiParam(params, "adquirientes", f.adquirientes);
  appendMultiParam(params, "estatus", f.estatus);
  return jsonFetch(`/api/contratos/dashboard?${params.toString()}`);
}

export function getContratosDetalle(opts: {
  anio: number;
  emisor?: string | null;
  adquiriente?: string | null;
  estatus?: string[];
  limit?: number;
  offset?: number;
}): Promise<{ items: ContratoListItem[]; total: number }> {
  const params = new URLSearchParams();
  params.set("anio", String(opts.anio));
  if (opts.emisor != null) params.set("emisor", opts.emisor);
  if (opts.adquiriente != null) params.set("adquiriente", opts.adquiriente);
  appendMultiParam(params, "estatus", opts.estatus);
  if (opts.limit != null) params.set("limit", String(opts.limit));
  if (opts.offset != null) params.set("offset", String(opts.offset));
  return jsonFetch(`/api/contratos/detalle?${params.toString()}`);
}

export function getContratosGantt(
  f: ContratoGanttFilters,
): Promise<{ items: ContratoGanttRow[]; total: number }> {
  const params = new URLSearchParams();
  appendMultiParam(params, "emisores", f.emisores);
  appendMultiParam(params, "adquirientes", f.adquirientes);
  appendMultiParam(params, "estatus", f.estatus);
  if (f.desde) params.set("desde", f.desde);
  if (f.hasta) params.set("hasta", f.hasta);
  if (f.agrupar_por) params.set("agrupar_por", f.agrupar_por);
  if (f.limit != null) params.set("limit", String(f.limit));
  if (f.offset != null) params.set("offset", String(f.offset));
  return jsonFetch(`/api/contratos/gantt?${params.toString()}`);
}

// ============================================================
// Demanda real de retiros (SIN) por centro de control — entrega incremental + CSV
// ============================================================
export type DemandaRetirosVista = "agregado" | "detalle";

export type DemandaRetirosFilters = {
  vista: DemandaRetirosVista;
  /** Corte incremental: filas con fecha_publicacion >= esta fecha (YYYY-MM-DD). */
  publicado_desde?: string;
  desde?: string;
  hasta?: string;
  ccr?: string;
  limit?: number;
  offset?: number;
};

export type DemandaRetirosResponse = {
  vista: DemandaRetirosVista;
  columns: string[];
  items: Record<string, string | number | null>[];
  has_more: boolean;
  offset: number;
};

export type DemandaRetirosResumen = {
  max_fecha_publicacion: string | null;
  max_fecha_operacion: string | null;
  max_actualizado_en: string | null;
  publicado_desde: string | null;
  nuevo: {
    filas: number;
    dias_operacion: number;
    op_desde: string | null;
    op_hasta: string | null;
  } | null;
};

function retirosParams(f: Partial<DemandaRetirosFilters>): URLSearchParams {
  const p = new URLSearchParams();
  if (f.vista) p.set("vista", f.vista);
  if (f.publicado_desde) p.set("publicado_desde", f.publicado_desde);
  if (f.desde) p.set("desde", f.desde);
  if (f.hasta) p.set("hasta", f.hasta);
  if (f.ccr) p.set("ccr", f.ccr);
  if (f.limit != null) p.set("limit", String(f.limit));
  if (f.offset != null) p.set("offset", String(f.offset));
  return p;
}

export function getDemandaRetiros(f: DemandaRetirosFilters): Promise<DemandaRetirosResponse> {
  return jsonFetch<DemandaRetirosResponse>(`/api/demanda-retiros?${retirosParams(f).toString()}`);
}

export function getDemandaRetirosCcrs(): Promise<{ items: string[] }> {
  return jsonFetch<{ items: string[] }>("/api/demanda-retiros/ccrs");
}

export function getDemandaRetirosResumen(publicadoDesde?: string): Promise<DemandaRetirosResumen> {
  const p = new URLSearchParams();
  if (publicadoDesde) p.set("publicado_desde", publicadoDesde);
  const qs = p.toString();
  return jsonFetch<DemandaRetirosResumen>(`/api/demanda-retiros/resumen${qs ? `?${qs}` : ""}`);
}

export async function downloadDemandaRetirosCsv(
  f: Omit<DemandaRetirosFilters, "limit" | "offset">,
): Promise<void> {
  const res = await fetchAuth(`/api/demanda-retiros/export.csv?${retirosParams(f).toString()}`);
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* sin cuerpo JSON */
    }
    throw new Error(`Descarga falló: ${detail}`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  a.download = match ? match[1] : "demanda_retiros.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}
